import { DestroyRef, Injectable, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { ModalController } from "@ionic/angular/standalone";
import { Capacitor, type PluginListenerHandle } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Device } from "@capacitor/device";
import { Functions, httpsCallableData } from "@angular/fire/functions";
import { combineLatest, filter, firstValueFrom, take } from "rxjs";
import { OtaUpdate } from "@b3app/capacitor-ota-update";
import { environment } from "../../../environments/environment";
import { LocalStorageService } from "./local-storage.service";
import { getInitContainers, getInitUser } from "../selectors/utility.selectors";
import { OTA_RESTART_SHEET_ID, OtaRestartSheetComponent } from "./ota-restart-sheet.component";

/* eslint-disable no-console --
   Instrumentation du cycle de mise à jour OTA (version courante, résultat du check,
   téléchargement) : niveau info assumé, les erreurs passent en console.warn. Seule trace
   exploitable pour diagnostiquer un déploiement OTA sur un device distant. */

// Clé localStorage : URL d'une route /event/... à restaurer après un reload OTA.
const OTA_PENDING_ROUTE_KEY = "ota_pending_route";

// Clé localStorage : dernier releaseId dont l'installation a été confirmée au serveur. Évite de
// reconfirmer (donc de re-compter) la même release à chaque démarrage — une confirmation par installation.
const OTA_CONFIRMED_RELEASE_KEY = "ota_confirmed_release_id";

// Clé localStorage : canal OTA choisi au runtime (programme bêta). Quand présent, il prime sur
// environment.ota.channelId — le build dédié reste la valeur par défaut/de secours.
const OTA_CHANNEL_OVERRIDE_KEY = "ota_channel_override";

// Throttle du check au resume : si un check (démarrage / périodique / resume) a eu lieu
// il y a moins de 5 min, on ne refait pas d'appel réseau au retour au premier plan.
// Réduit fortement le nombre d'appels quand `checkOnResume` est activé (resumes rapprochés).
const RESUME_CHECK_THROTTLE_MS = 5 * 60 * 1000;

@Injectable({ providedIn: "root" })
export class OtaService {
	private readonly modalCtrl = inject(ModalController);
	private readonly destroyRef = inject(DestroyRef);
	private readonly router = inject(Router);
	private readonly localStorage = inject(LocalStorageService);
	private readonly store = inject(Store);
	private readonly functions = inject(Functions);

	readonly updateAvailable = signal(false);
	readonly currentVersion = signal<string | null>(null);
	readonly checking = signal(false);
	// Version native (binaire) : { version, build } via App.getInfo(). null tant que non lue (web).
	readonly nativeVersion = signal<{ version: string; build: string } | null>(null);
	// Canal OTA forcé au runtime (programme bêta) ; null = canal du build (environment.ota.channelId).
	readonly channelOverrideId = signal<string | null>(this.localStorage.getItem<string>(OTA_CHANNEL_OVERRIDE_KEY));

	private intervalHandle?: ReturnType<typeof setInterval>;
	private resumeListener?: PluginListenerHandle;
	// Anti double-init (HMR, tests, reload manuel).
	private initialized = false;
	// Empêche d'empiler plusieurs feuilles de redémarrage en même temps.
	private restartSheetOpen = false;
	// MAJ téléchargée à appliquer silencieusement (mode channel `silentUpdate`).
	private silentPending = false;
	// Intervalle de polling courant (minutes), piloté par le channel (0 = désactivé).
	private currentIntervalMinutes?: number;
	// Vérification au resume activée par le channel (coûteuse — clients VIP). Défaut false.
	private checkOnResume = false;
	// Horodatage du dernier check réseau (startup/périodique/resume) — base du throttle resume.
	private lastCheckAt = 0;
	// Release déjà téléchargée (pending) : évite un re-téléchargement à chaque check.
	private downloadedReleaseId?: string;
	// Garde de ré-entrance : empêche deux applications silencieuses concurrentes
	// (deux `appStateChange` rapprochés) de se chevaucher.
	private applyingSilent = false;
	// Un seul resetToEmbedded par cold start (évite reload infini si le check
	// renvoie encore native_version_too_old après reload).
	private resetToEmbeddedAttempted = false;

	async initialize(): Promise<void> {
		// Anti double-init (HMR, tests, reload manuel). On ne consomme le drapeau qu'une
		// fois la plateforme native confirmée (sinon un appel web bloquerait un init natif).
		if (this.initialized) {
			return;
		}
		if (!Capacitor.isNativePlatform()) {
			return;
		}
		this.initialized = true;

		// Désarme le watchdog natif anti-crash : signale que l'app web a démarré, donc que
		// le bundle courant est sain (sinon rollback automatique au bout de N boots non
		// confirmés). Indépendant de `ota.enabled` car le watchdog tourne dès que le plugin
		// natif est présent (y compris sur le build invent-app sans bloc `ota`).
		OtaUpdate.notifyAppReady().catch((e) => console.warn("[OTA] notifyAppReady failed", e));

		// Restaure la route /event/... éventuellement sauvegardée avant un reload OTA.
		// Indépendant de `ota.enabled` : la clé n'existe que si un reload a eu lieu.
		this.restoreRouteAfterOtaReload();

		// `?.` défensif : si l'app est buildée avec une configuration sans bloc `ota`
		// (ex: environment.inventapp.ts), on ne fait rien de plus plutôt que de planter.
		if (!environment.ota?.enabled) {
			return;
		}

		try {
			const info = await OtaUpdate.getCurrentVersion();
			this.currentVersion.set(info.version);
			console.warn("[OTA] Current version:", info.version);
			// Confirme l'installation de la release active au serveur (une seule fois par release).
			// Non bloquant : ne doit jamais retarder ni casser le démarrage.
			void this.confirmInstallOnce(info.releaseId);
		} catch (e) {
			console.warn("[OTA] Could not read current version", e);
		}

		try {
			const appInfo = await App.getInfo();
			this.nativeVersion.set({ version: appInfo.version, build: appInfo.build });
		} catch (e) {
			console.warn("[OTA] Could not read native version", e);
		}

		if (environment.ota.checkOnStartup) {
			this.checkAndDownload().catch((e) => console.warn("[OTA] Initial check failed", e));
		}

		// Le timer périodique est entièrement piloté par le channel (armé/annulé via
		// applyServerInterval au résultat des checks) : rien à armer ici, juste le nettoyage.
		this.destroyRef.onDestroy(() => {
			if (this.intervalHandle) {
				clearInterval(this.intervalHandle);
				this.intervalHandle = undefined;
			}
		});

		// À chaque retour au premier plan : re-présenter la MAJ en attente (téléchargée mais
		// pas encore appliquée) ou vérifier immédiatement — évite d'attendre l'interval
		// périodique après un déploiement back office.
		this.resumeListener = await App.addListener("appStateChange", ({ isActive }) => {
			if (isActive) {
				void this.onAppResume();
			}
		});
		this.destroyRef.onDestroy(() => {
			void this.resumeListener?.remove();
			this.resumeListener = undefined;
		});
	}

	async checkAndDownload(): Promise<void> {
		if (this.checking()) return;
		this.checking.set(true);
		this.lastCheckAt = Date.now();

		try {
			const check = await OtaUpdate.checkForUpdate({
				appId: environment.ota.appId,
				// Override runtime (programme bêta) prioritaire ; sinon canal figé au build.
				channelId: this.channelOverrideId() ?? environment.ota.channelId,
				apiBaseUrl: environment.ota.apiBaseUrl
			});

			console.warn("[OTA] Check result:", check);

			// Config de diffusion pilotée par le channel (back office) : intervalle
			// périodique (0 = annule le timer) + check au resume (clients VIP).
			if (typeof check.checkIntervalMinutes === "number") {
				this.applyServerInterval(check.checkIntervalMinutes);
			}
			if (typeof check.checkOnResume === "boolean") {
				this.checkOnResume = check.checkOnResume;
			}

			if (!check.updateAvailable) {
				console.warn("[OTA] No update available, reason:", check.reason);
				// Native sous le min du canal + OTA encore actif = risque d'écran blanc
				// (JS stale sur binaire Cap majeur). Reset vers le www embarqué une fois.
				if (check.reason === "native_version_too_old" && !this.resetToEmbeddedAttempted) {
					const activeVersion = this.currentVersion();
					if (activeVersion) {
						this.resetToEmbeddedAttempted = true;
						console.warn(
							"[OTA] native_version_too_old with active OTA",
							activeVersion,
							"— resetToEmbedded"
						);
						await OtaUpdate.resetToEmbedded();
						return;
					}
				}
				return;
			}

			// Évite de re-télécharger une release déjà récupérée (pending) à chaque check.
			if (check.releaseId !== this.downloadedReleaseId) {
				console.warn("[OTA] Downloading version", check.version);
				const dl = await OtaUpdate.downloadUpdate({
					bundleUrl: check.bundleUrl,
					signature: check.signature,
					hashSha256: check.hashSha256,
					releaseId: check.releaseId,
					version: check.version
				});

				if (!dl.success) {
					console.warn("[OTA] Download failed:", dl.errorMessage);
					return;
				}

				console.warn("[OTA] Update downloaded successfully");
				this.downloadedReleaseId = check.releaseId!;
				this.updateAvailable.set(true);

				// Présentation de la feuille (ou application silencieuse) UNIQUEMENT pour une release
				// fraîchement téléchargée : sinon, avec un `checkIntervalMinutes` court, elle se
				// ré-afficherait à chaque tick → pop-up en boucle. Le re-rappel d'une MAJ en attente
				// reste assuré au retour au premier plan (onAppResume).
				if (check.silentUpdate) {
					// Mode silencieux : appliquer sans pop-up (au resume / prochain démarrage à froid).
					await OtaUpdate.applyUpdateOnNextLaunch();
					this.silentPending = true;
				} else {
					await this.presentRestartSheet();
				}
			}
		} catch (e) {
			console.warn("[OTA] Check failed", e);
		} finally {
			this.checking.set(false);
		}
	}

	async applyAndReload(): Promise<void> {
		this.saveRouteForOtaReload();
		await OtaUpdate.applyUpdateAndReload();
	}

	// Confirme au serveur que ce device tourne désormais sur `releaseId` (comptage d'installations OTA).
	// Dédupliqué via localStorage : une seule confirmation par release. Échec silencieux (non critique).
	private async confirmInstallOnce(releaseId: string | null): Promise<void> {
		if (!releaseId) {
			return;
		}
		if (this.localStorage.getItem<string>(OTA_CONFIRMED_RELEASE_KEY) === releaseId) {
			return;
		}
		try {
			const deviceId = (await Device.getId()).identifier;
			await firstValueFrom(
				httpsCallableData<
					{ appId: string; releaseId: string; deviceId: string; platform: string },
					{ ok: boolean }
				>(
					this.functions,
					"mobileDeployments-confirmOtaInstall",
					{}
				)({
					appId: environment.ota.appId,
					releaseId,
					deviceId,
					platform: Capacitor.getPlatform()
				})
			);
			this.localStorage.setItem(OTA_CONFIRMED_RELEASE_KEY, releaseId);
		} catch (e) {
			console.warn("[OTA] confirmInstall failed (non-bloquant)", e);
		}
	}

	// Sauvegarde l'URL courante avant un reload OTA si on est sur une route détail
	// d'événement, pour y revenir après le redémarrage (le reload repart de la base).
	private saveRouteForOtaReload(): void {
		const url = this.router.url;
		if (url.startsWith("/event/")) {
			this.localStorage.setItem(OTA_PENDING_ROUTE_KEY, { url, ts: Date.now() });
		}
	}

	// Après un reload OTA : si une route /event/... a été sauvegardée récemment, on y
	// redirige une fois la navigation de démarrage stabilisée (sinon elle serait écrasée
	// par la nav d'init/auth). One-shot + TTL pour ne jamais restaurer une route périmée.
	private restoreRouteAfterOtaReload(): void {
		const saved = this.localStorage.getItem<{ url: string; ts: number }>(OTA_PENDING_ROUTE_KEY);
		this.localStorage.removeItem(OTA_PENDING_ROUTE_KEY);
		if (!saved || !saved.url.startsWith("/event/") || Date.now() - saved.ts > 120_000) {
			return;
		}
		// Attendre que l'auth ET les containers soient initialisés (mêmes signaux que
		// EventGuard) plutôt que de se fier au timing d'un NavigationEnd : déterministe,
		// garantit que la garde laissera passer la navigation, et que la redirection part
		// après la nav de démarrage (donc sans être écrasée).
		combineLatest([
			this.store.select(getInitUser).pipe(
				filter((init) => init === true),
				take(1)
			),
			this.store.select(getInitContainers).pipe(
				filter((init) => init === true),
				take(1)
			)
		])
			.pipe(take(1), takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				if (this.router.url !== saved.url) {
					void this.router.navigateByUrl(saved.url, { replaceUrl: true });
				}
			});
	}

	// (Re)programme ou annule le timer de polling selon l'intervalle (minutes) du channel.
	// 0 (ou négatif) = vérification périodique désactivée. No-op si déjà appliqué.
	private applyServerInterval(minutes: number): void {
		if (minutes === this.currentIntervalMinutes) {
			return;
		}
		this.currentIntervalMinutes = minutes;
		if (this.intervalHandle) {
			clearInterval(this.intervalHandle);
			this.intervalHandle = undefined;
		}
		if (minutes > 0) {
			this.intervalHandle = setInterval(
				() => {
					this.checkAndDownload().catch((e) => console.warn("[OTA] Periodic check failed", e));
				},
				minutes * 60 * 1000
			);
		}
	}

	// Déclenché au retour au premier plan (resume).
	private async onAppResume(): Promise<void> {
		// Re-signale le boot sain au watchdog natif à chaque retour.
		OtaUpdate.notifyAppReady().catch(() => undefined);

		if (!environment.ota?.enabled) {
			return;
		}

		if (this.silentPending) {
			// Mode silencieux : appliquer la MAJ déjà téléchargée au retour au premier plan.
			// Garde de ré-entrance pour ne pas empiler deux applications ; on NE remet PAS
			// `silentPending` à false (un channel silencieux ne doit jamais retomber sur la
			// pop-up). L'application native est idempotente : le pending n'est consommé
			// qu'une fois, et le reload remplace le contexte JS.
			if (this.applyingSilent) {
				return;
			}
			this.applyingSilent = true;
			try {
				await this.applyAndReload();
			} finally {
				this.applyingSilent = false;
			}
			return;
		}

		if (this.updateAvailable()) {
			// MAJ déjà téléchargée mais non appliquée (ex. échec d'application) : on re-présente
			// la modale obligatoire. Pas d'appel réseau.
			await this.presentRestartSheet();
		} else if (this.checkOnResume && Date.now() - this.lastCheckAt >= RESUME_CHECK_THROTTLE_MS) {
			// Check réseau au resume : channels VIP uniquement (coûteux), throttlé à 5 min
			// pour ne pas multiplier les appels sur des retours au premier plan rapprochés.
			await this.checkAndDownload();
		}
	}

	// Modale bloquante de mise à jour obligatoire (aucun moyen de l'ignorer).
	// Le verrouillage repose sur `canDismiss` (seul le rôle `restart` passe) : tap backdrop,
	// bouton retour Android et Escape tentent un dismiss avec le rôle `backdrop`, refusé.
	// Surtout PAS `backdropDismiss: false` : Ionic n'enregistre ses handlers retour/Escape sur
	// l'overlay que si le backdrop est dismissable — sinon l'appui retour traverse la modale
	// et fait naviguer la page en dessous (NavController.pop).
	// Garde `restartSheetOpen` : jamais deux feuilles présentées simultanément.
	private async presentRestartSheet(): Promise<void> {
		if (this.restartSheetOpen) {
			return;
		}
		this.restartSheetOpen = true;
		try {
			const modal = await this.modalCtrl.create({
				id: OTA_RESTART_SHEET_ID,
				component: OtaRestartSheetComponent,
				cssClass: "ota-restart-sheet-modal",
				canDismiss: (_data, role) => Promise.resolve(role === "restart")
			});
			await modal.present();
			const { role } = await modal.onWillDismiss();
			if (role === "restart") {
				await this.applyAndReload();
			}
		} finally {
			this.restartSheetOpen = false;
		}
	}

	// ─── Programme bêta (opt-in / opt-out OTA au runtime) ─────────────────────

	// Statut du programme bêta pour l'utilisateur connecté + ce device (pilote l'UI).
	async getBetaStatus(): Promise<{ eligible: boolean; onBeta: boolean; betaChannelId: string | null }> {
		const deviceId = (await Device.getId()).identifier;
		return firstValueFrom(
			httpsCallableData<
				{ appId: string; deviceId: string },
				{ eligible: boolean; onBeta: boolean; betaChannelId: string | null }
			>(
				this.functions,
				"mobileDeployments-getBetaProgramStatus",
				{}
			)({ appId: environment.ota.appId, deviceId })
		);
	}

	// Rejoint le programme bêta : lie le device côté serveur, bascule le canal OTA sur la bêta
	// (override local prioritaire) et déclenche un check (téléchargement + proposition de redémarrage).
	async joinBeta(): Promise<void> {
		const deviceId = (await Device.getId()).identifier;
		const res = await firstValueFrom(
			httpsCallableData<{ appId: string; deviceId: string }, { betaChannelId: string }>(
				this.functions,
				"mobileDeployments-joinBetaProgram",
				{}
			)({ appId: environment.ota.appId, deviceId })
		);
		this.localStorage.setItem(OTA_CHANNEL_OVERRIDE_KEY, res.betaChannelId);
		this.channelOverrideId.set(res.betaChannelId);
		// Repart d'un check propre sur le canal bêta.
		this.downloadedReleaseId = undefined;
		await this.checkAndDownload();
	}

	// Quitte le programme bêta : délie le device, repasse au canal live et applique en force la
	// release live renvoyée par le serveur (retour garanti même si la bêta avait un numéro supérieur).
	async leaveBeta(): Promise<void> {
		const deviceId = (await Device.getId()).identifier;
		const res = await firstValueFrom(
			httpsCallableData<
				{ appId: string; deviceId: string },
				{
					liveRelease: {
						releaseId: string;
						version: string;
						bundleUrl: string;
						signature: string;
						hashSha256: string;
						bundleSize: number;
					} | null;
				}
			>(
				this.functions,
				"mobileDeployments-leaveBetaProgram",
				{}
			)({ appId: environment.ota.appId, deviceId })
		);

		this.localStorage.removeItem(OTA_CHANNEL_OVERRIDE_KEY);
		this.channelOverrideId.set(null);
		this.downloadedReleaseId = undefined;
		// Nettoyage de l'état OTA bêta en attente : sinon une MAJ bêta silencieuse ou déjà téléchargée
		// serait (ré)appliquée au prochain resume APRÈS avoir quitté le programme (cf. onAppResume).
		this.silentPending = false;
		this.updateAvailable.set(false);

		const live = res.liveRelease;
		if (live) {
			const dl = await OtaUpdate.downloadUpdate({
				bundleUrl: live.bundleUrl,
				signature: live.signature,
				hashSha256: live.hashSha256,
				releaseId: live.releaseId,
				version: live.version
			});
			if (dl.success) {
				this.downloadedReleaseId = live.releaseId;
				this.updateAvailable.set(true);
				await this.presentRestartSheet();
			} else {
				console.warn("[OTA] leaveBeta: download failed", dl.errorMessage);
			}
		}
	}
}
